import React, { useCallback, useMemo } from 'react';
import { WriteBackToWorkdayViaWsPlaceToWrite } from 'libs/end-to-end/src/types/databases/workflows/strategies/workday-write-back';
import { WriteBackToWorkdayViaWsStrategy } from 'libs/end-to-end/src/types/databases/workflows/strategies/workday-write-back';
import { ConfigureWorkflowBaseProps } from 'apps/sigma2-ui/src/components/workflows/types';
import { DataSetSecretSelector } from 'apps/sigma2-ui/src/data-wizard/components/DataSetSecretSelector';
import { SecretReference } from 'libs/end-to-end/src/types/secrets/secret-reference';
import { WorkflowPlaces } from 'apps/sigma2-ui/src/components/workflows/WorkflowPlaces';
import { ConfigureWriteBackWorkdayViaWsPlaceToWrite } from 'apps/sigma2-ui/src/components/workflows/ConfigureWriteBackWorkdayViaWsPlaceToWrite';

export interface ConfigureWriteBackWorkdayViaWsStrategyProps
  extends ConfigureWorkflowBaseProps {
  strategy: WriteBackToWorkdayViaWsStrategy | null;

  updateStrategy: (strategy: WriteBackToWorkdayViaWsStrategy | null) => void;
}

export const ConfigureWriteBackWorkdayViaWsStrategy = ({
  strategy,
  updateStrategy,
  listSecretsPattern,
  organizationId,
  listDataSetsPattern,
}: ConfigureWriteBackWorkdayViaWsStrategyProps) => {
  const updateStrategyPartial = useCallback(
    (partial: Partial<WriteBackToWorkdayViaWsStrategy>) => {
      updateStrategy({
        type: 'workdayViaWs',
        ...strategy,
        ...partial,
      });
    },
    [strategy, updateStrategy],
  );

  const updatePlacesToWrite = useCallback(
    (placesToWrite: WriteBackToWorkdayViaWsPlaceToWrite[]) => {
      updateStrategyPartial({
        placesToWrite,
      });
    },
    [updateStrategyPartial],
  );

  const updateWorkdaySoapVersion = useCallback(
    (workdaySoapVersion: string) => {
      updateStrategyPartial({
        workdaySoapVersion,
      });
    },
    [updateStrategyPartial],
  );

  const updateEndpoint = useCallback(
    (endpoint: string) => {
      updateStrategyPartial({
        endpoint,
      });
    },
    [updateStrategyPartial],
  );

  const selectedPlacesToWrite = useMemo(
    () => strategy?.placesToWrite ?? [],
    [strategy?.placesToWrite],
  );

  const updateUsernameSecretReference = useCallback(
    (authUsernameSecretReference: SecretReference | null) => {
      updateStrategyPartial({
        authUsernameSecretReference: authUsernameSecretReference ?? undefined,
      });
    },
    [updateStrategyPartial],
  );

  const updatePasswordSecretReference = useCallback(
    (authPasswordSecretReference: SecretReference | null) => {
      updateStrategyPartial({
        authPasswordSecretReference: authPasswordSecretReference ?? undefined,
      });
    },
    [updateStrategyPartial],
  );

  return (
    <div className="space-y-4 p-4 bg-white rounded shadow">
      <h2 className="text-xl font-semibold">
        Configure Workday via WS Strategy
      </h2>
      <p className="text-gray-600">
        Configure the strategy for writing back the hiring readiness score to
        Workday via WS.
      </p>

      <WorkflowPlaces
        placesToWrite={selectedPlacesToWrite}
        updatePlacesToWrite={updatePlacesToWrite}
        placeToWriteComponent={({ setNewPlaceToWrite, newPlaceToWrite }) => (
          <ConfigureWriteBackWorkdayViaWsPlaceToWrite
            placeToWrite={newPlaceToWrite}
            updatePlaceToWrite={setNewPlaceToWrite}
            listDataSetsPattern={listDataSetsPattern}
            listSecretsPattern={listSecretsPattern}
            organizationId={organizationId}
          />
        )}
      />

      <label className="block text-sm font-medium text-gray-700">
        Workday SOAP Version
      </label>
      <input
        className="form-input mt-1 block w-full rounded-md border-gray-300 shadow-sm"
        type="text"
        value={strategy?.workdaySoapVersion ?? ''}
        onChange={(e) => {
          updateWorkdaySoapVersion(e.target.value);
        }}
      />

      <label className="block text-sm font-medium text-gray-700">
        Workday Endpoint URL
      </label>
      <input
        className="form-input mt-1 block w-full rounded-md border-gray-300 shadow-sm"
        type="text"
        value={strategy?.endpoint ?? ''}
        onChange={(e) => {
          updateEndpoint(e.target.value);
        }}
      />

      <DataSetSecretSelector
        secretReference={strategy?.authUsernameSecretReference ?? null}
        setSecretReference={updateUsernameSecretReference}
        organizationId={organizationId}
        label="Select the Workday username secret"
        listSecretsPattern={listSecretsPattern}
      />

      <DataSetSecretSelector
        secretReference={strategy?.authPasswordSecretReference ?? null}
        setSecretReference={updatePasswordSecretReference}
        organizationId={organizationId}
        label="Select the Workday password secret"
        listSecretsPattern={listSecretsPattern}
      />
    </div>
  );
};
